import moment from 'moment';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import styled, { css, StyledComponentPropsWithRef } from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import {
  hideLoadingSpinner,
  showLoadingSpinner,
} from '~/javascripts/admin/actions/loading-spinner';
import {
  fetchPrivateMoneyAccountHooks,
  updatePrivateMoneyAccountHook,
} from '~/javascripts/admin/actions/private-money-account-hooks';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import { formatDate, toQueryParams } from '~/javascripts/admin/utils';
import { createColorCodedOptions } from '../../../atoms/ColorCodedOption';
import FA from '../../../atoms/FontAwesome';
import Link from '../../../atoms/Link';
import Select from '../../../atoms/Select';
import Table from '../../../atoms/Table';
import ApiResults from '../../../molecules/ApiResults';
import Pager from '../../../shared/Pager';

type Message = StyledComponentPropsWithRef<typeof ApiResults>['messages'];

type Props = {
  privateMoneyId: string;
  setApiResult: Dispatch<SetStateAction<Message | undefined>>;
};

type PrivateMoneyAccountHooks = Awaited<ReturnType<typeof fetchPrivateMoneyAccountHooks>>;

const ModalWarningTitle = styled.p`
  ${({ theme }) => css`
    ${theme.typography.heading3.bold};
    color: ${theme.colors.gray[400]};
  `}
`;

const ModalWarningMessage = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: 0.5rem;
    justify-content: center;

    p {
      ${theme.typography.body1.regular}
      color: ${theme.colors.red[400]};
      margin-bottom: 0;
    }
  `}
`;

const Icon = styled(FA)`
  font-size: 16px;
`;

const DATE_FORMAT = 'YYYY/MM/DD HH:mm';

const ENABLE_OPTIONS = [
  {
    label: '有効',
    value: true,
    bgColor: '#21BF25',
  },
  {
    label: '無効',
    value: false,
    bgColor: '#D0D0d0',
  },
];

const formatOptionLabel = createColorCodedOptions(ENABLE_OPTIONS);

const MoneyAccountHooksTable = ({ privateMoneyId, setApiResult }: Props) => {
  const dispatch = useAppDispatch();
  const [privateMoneyAccountHooks, setPrivateMoneyAccountHooks] =
    useState<PrivateMoneyAccountHooks>();

  const getPrivateMoneyAccountHooks = useCallback(
    (page = 1) => {
      if (privateMoneyId) {
        fetchPrivateMoneyAccountHooks(
          toQueryParams({
            private_money_id: privateMoneyId,
            page,
          })
        ).then((res) => setPrivateMoneyAccountHooks(res));
      }
    },
    [privateMoneyId]
  );

  const onChangePage = (page: number) => {
    getPrivateMoneyAccountHooks(page);
  };

  const handleUpdateEnableStatus = (
    checked: boolean,
    privateMoneyAccountHook: PrivateMoneyAccountHooks['rows'][number]
  ) => {
    const hasEnabledHook = privateMoneyAccountHooks?.rows?.some(
      (p) => p.is_disabled === false && p.id !== privateMoneyAccountHook.id
    );
    if (checked && hasEnabledHook) {
      dispatch(
        openAlertModal({
          body: (
            <div>
              <ModalWarningTitle>ウォレット追加キャンペーンが重複しています</ModalWarningTitle>
              <ModalWarningMessage>
                <Icon icon="exclamation-triangle" color="#D75F48" />
                <p>
                  有効なキャンペーンは１件までです。既存のキャンペーンを無効にしてから、再度操作してください
                </p>
              </ModalWarningMessage>
            </div>
          ),
          confirmText: 'OK',
          onConfirm: () => null,
          useCancel: false,
        })
      );
      return;
    }

    dispatch(showLoadingSpinner());
    updatePrivateMoneyAccountHook(privateMoneyAccountHook.id, {
      private_money_id: privateMoneyId,
      is_disabled: !checked,
    })
      .then(() => {
        getPrivateMoneyAccountHooks();
      })
      .catch((err) => {
        setApiResult([
          {
            text: err.message,
            isError: true,
          },
        ]);
      })
      .finally(() => {
        dispatch(hideLoadingSpinner());
        setTimeout(() => setApiResult(undefined), 4000);
      });
  };

  useEffect(() => {
    getPrivateMoneyAccountHooks();
  }, [privateMoneyId, getPrivateMoneyAccountHooks]);

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.Cell size="1">ID</Table.Cell>
          <Table.Cell size="1.5">開始日時</Table.Cell>
          <Table.Cell size="1.5">終了日時</Table.Cell>
          <Table.Cell size="2">キャンペーン名</Table.Cell>
          <Table.Cell size="2">説明</Table.Cell>
          <Table.Cell align="right">ポイント</Table.Cell>
          <Table.Cell align="right">使用人数</Table.Cell>
          <Table.Cell width="160px">状態</Table.Cell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {privateMoneyAccountHooks?.rows?.map((privateMoneyAccountHook) => (
          <Table.Row
            key={privateMoneyAccountHook.id}
            style={{ background: privateMoneyAccountHook.is_disabled ? '#ddd' : 'unset' }}
          >
            <Table.Cell>
              <Link
                to={`/promotion/private-moneys/${privateMoneyId}/money-account-hooks/${privateMoneyAccountHook.id}`}
              >
                {privateMoneyAccountHook.id}
              </Link>
            </Table.Cell>
            <Table.Cell>{formatDate(privateMoneyAccountHook.starts_at, DATE_FORMAT)}</Table.Cell>
            <Table.Cell>
              {privateMoneyAccountHook?.ends_at &&
                formatDate(
                  moment(privateMoneyAccountHook.ends_at).subtract(1, 'day').endOf('day'),
                  DATE_FORMAT
                )}
            </Table.Cell>
            <Table.Cell wordBreak="break-word">{privateMoneyAccountHook.name}</Table.Cell>
            <Table.Cell wordBreak="break-word">{privateMoneyAccountHook.description}</Table.Cell>
            <Table.Cell align="right">
              {privateMoneyAccountHook.point_amount?.toLocaleString()}
            </Table.Cell>
            <Table.Cell align="right" wordBreak="keep-all">
              {`${privateMoneyAccountHook.usage_count?.toLocaleString()} ${
                privateMoneyAccountHook.usage_limit
                  ? `/ ${privateMoneyAccountHook.usage_limit?.toLocaleString()}`
                  : '(上限無し)'
              }`}
            </Table.Cell>
            <Table.Cell>
              <Select
                options={ENABLE_OPTIONS}
                value={ENABLE_OPTIONS.find((x) => x.value === !privateMoneyAccountHook.is_disabled)}
                isMulti={false}
                formatOptionLabel={formatOptionLabel}
                onChange={(option) =>
                  handleUpdateEnableStatus(option?.value ?? false, privateMoneyAccountHook)
                }
              />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row noBorder>
          <Table.Cell colSpan={8} align="center" styles={{ paddingTop: '16px' }}>
            <Pager pagination={privateMoneyAccountHooks?.pagination} onChangePage={onChangePage} />
          </Table.Cell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default styled(MoneyAccountHooksTable)``;
